import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/hooks/useI18n';
import {
    IInstallmentDraft,
    IInstallment,
    NEW_INSTALLMENT_REMINDERS,
} from '@/lib/builder/models/payment-plan.model';
import {
    toCents,
    fromCents,
    sumCents,
    splitEvenly,
    isPaidIn,
    dueDateBounds,
    nextInstallmentDueDate,
    PlanValidation,
} from '@/lib/builder/utils/payment-plan.utils';
import { InstallmentEditorRow } from './InstallmentEditorRow';

interface PaymentPlanEditorProps {
    draft: IInstallmentDraft[];
    itineraryTotal: number;
    /** Rate incassate, per id: servono a bloccare importo e scadenza. */
    paidById: Record<string, IInstallment>;
    /** Calcolata dalla card, che possiede la bozza: qui serve solo per evidenziare i campi. */
    validation: PlanValidation;
    formatAmount: (value: number) => string;
    /** Rata da evidenziare all'apertura, quando si arriva da una riga della dashboard. */
    highlightInstallmentId?: string | null;
    onDraftChange: (draft: IInstallmentDraft[]) => void;
}

/**
 * Corpo dell'editor del piano rate. Vive dentro una modale: il riepilogo della quadratura
 * e i pulsanti Annulla/Salva stanno nel footer della modale, non qui, così restano
 * visibili anche con molte rate.
 *
 * Non c'e' una scelta fra "saldo unico" e "a rate": il piano nasce con una riga sola per
 * l'intero importo, e chi vuole rateizzare aggiunge righe. Erano due modalita' solo in
 * apparenza — un piano da una rata e' gia' il pagamento unico — e tenerle separate faceva
 * si' che un incasso avvenuto in una modalita' non risultasse nell'altra.
 */
export const PaymentPlanEditor = ({
    draft,
    itineraryTotal,
    paidById,
    validation,
    formatAmount,
    highlightInstallmentId,
    onDraftChange,
}: PaymentPlanEditorProps) => {
    const { t } = useI18n();
    const isPaid = isPaidIn(paidById);

    const patchRow = (index: number, patch: Partial<IInstallmentDraft>) => {
        onDraftChange(draft.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    };

    const removeRow = (index: number) => {
        onDraftChange(draft.filter((_, i) => i !== index));
    };

    /**
     * Quanto del totale non e' ancora coperto dalle righe in tabella. E' il caso che conta:
     * il viaggio e' gia' saldato, il TD aggiunge un'attivita' e il totale sale, quindi
     * torna qui per incassare la differenza. Le rate gia' pagate non si possono toccare, e
     * la riga nuova deve valere esattamente il residuo — riproporzionare non e' un'opzione.
     *
     * A piano quadrato il residuo e' zero: la riga nasce a 0 e la validazione blocca il
     * salvataggio finche' il TD non decide gli importi. E' voluto — l'alternativa sarebbe
     * spostare soldi da una rata gia' comunicata al cliente senza dirlo.
     */
    const remainingCents = Math.max(0, toCents(itineraryTotal) - sumCents(draft));

    const addRow = () => {
        onDraftChange([
            ...draft,
            {
                label: t('checkout.paymentPlan.newInstallmentLabel', { index: draft.length + 1 }),
                amount: fromCents(remainingCents),
                // Mai il giorno di una rata gia' in tabella: la riga nascerebbe in errore.
                due_date: nextInstallmentDueDate(draft),
                notify_on_due: true,
                reminder_offsets: [...NEW_INSTALLMENT_REMINDERS],
            },
        ]);
    };

    return (
        /* Il corpo della modale non ha padding proprio: lo mette il contenuto. */
        <div className="flex flex-col gap-3 px-6 py-4">
            {/* La riga sola non e' un caso degenere del piano: e' il pagamento unico. Detto
                qui perche' senza il selettore di modalita' nulla lo direbbe piu'. */}
            <p className="text-xs text-gray-500">{t('checkout.paymentPlan.singleRowHint')}</p>

            {/* Lo scroll sta qui e non sul corpo della modale: con molte rate l'intestazione
                delle colonne uscirebbe dallo schermo proprio mentre si compilano le righe. */}
            <div className="overflow-auto border border-gray-200 rounded-lg max-h-[min(50vh,24rem)]">
                {/* Tutte le colonne hanno una larghezza: nessuna e' elastica, cosi' lo
                    spazio in piu' della modale viene ripartito in proporzione invece di
                    finire tutto in una sola. I promemoria stanno stretti e i chip vanno a
                    capo — sono la colonna che si consulta meno. */}
                <table className="w-full min-w-[780px] table-fixed text-sm">
                    <thead className="sticky top-0 z-10">
                        <tr className="bg-gray-50 [&>th]:py-1.5 [&>th]:px-1.5 [&>th]:text-[11px] [&>th]:font-semibold [&>th]:uppercase [&>th]:tracking-wide [&>th]:text-gray-500 [&>th]:bg-gray-50 [&>th]:border-b [&>th]:border-gray-200">
                            <th className="w-7 pl-3 text-left">#</th>
                            <th className="w-[208px] text-left">
                                {t('checkout.paymentPlan.columns.label')}
                            </th>
                            <th className="w-[172px] text-right">
                                {t('checkout.paymentPlan.columns.amount')}
                            </th>
                            <th className="w-[152px] text-left">
                                {t('checkout.paymentPlan.columns.dueDate')}
                            </th>
                            <th className="w-16 text-center">
                                {t('checkout.paymentPlan.columns.notifyOnDue')}
                            </th>
                            <th className="w-[132px] text-left">
                                {t('checkout.paymentPlan.columns.reminders')}
                            </th>
                            <th className="w-9" />
                        </tr>
                    </thead>
                    <tbody>
                        {draft.map((installment, index) => {
                            // Le scadenze devono crescere di riga in riga: ogni calendario
                            // vede solo i giorni liberi fra la rata precedente e la seguente.
                            const bounds = dueDateBounds(draft, index);

                            return (
                            <InstallmentEditorRow
                                key={installment.id ?? `new-${index}`}
                                installment={installment}
                                index={index}
                                isPaid={isPaid(installment)}
                                minDueDate={bounds.min}
                                maxDueDate={bounds.max}
                                errors={validation.fieldErrors[index]}
                                itineraryTotal={itineraryTotal}
                                formatAmount={formatAmount}
                                canRemove={draft.length > 1}
                                isHighlighted={
                                    Boolean(highlightInstallmentId) &&
                                    installment.id === highlightInstallmentId
                                }
                                onChange={patchRow}
                                onRemove={removeRow}
                            />
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center gap-2">
                <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={addRow}>
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    {t('checkout.paymentPlan.actions.addInstallment')}
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-gray-500"
                    onClick={() =>
                        onDraftChange(splitEvenly(draft, toCents(itineraryTotal), isPaid))
                    }
                >
                    {t('checkout.paymentPlan.actions.splitEvenly')}
                </Button>
            </div>
        </div>
    );
};
